// KPI modülü — /api/v1/kpi/* typed wrapper'ları (M16 T9; route tablosu: routes/kpi.ts).
// Hata kodları KpiError → ApiError.code (KPI_STALE_WRITE 409, KPI_DERIVED_READONLY 400 …).

import type {
  KpiBreakdown,
  KpiBreakdownConvertBody,
  KpiBreakdownConvertResult,
  KpiBreakdownCustomDimension,
  KpiBreakdownCustomDimensionPatch,
  KpiBreakdownDimensionsResponse,
  KpiBreakdownGroupCreateBody,
  KpiBreakdownGroupCreateResult,
  KpiFrameworkOption,
  KpiMetric,
  KpiMetricReorderResult,
  KpiMetricRequirement,
  KpiMetricUpsert,
  KpiResolvedGrid,
  KpiSet,
  KpiSetCreate,
  KpiSetIndicator,
  KpiSetIndicatorPatch,
  KpiSetUpdate,
  KpiValueUpsert,
  KpiValueVersion,
  SetFillStatus,
  KpiMetricRenameCodeResult,
  KpiUnitChangeBody,
  KpiUnitChangePreview,
  KpiUnitChangeResult,
  KpiUnitUsage,
} from "@mahir/shared";
import { api } from "./client";
import type { ClientPickerItem } from "../lib/client-picker";

export interface KpiClientSummary {
  id: string;
  name: string;
  sector: string | null;
  setCount: number;
  sets: {
    id: string;
    name: string;
    frameworkId: string | null;
    frameworkName: string | null;
    reportScope: string;
    indicatorCount: number;
    updatedAt: string;
  }[];
}

export interface KpiValueDto {
  id: string;
  clientId: string;
  metricId: string;
  entityId: string;
  year: number;
  periodType: string;
  status: string;
  numericValue: number | null;
  textValue: string | null;
  workflowState: string;
  isReported: boolean;
  pinned: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string | null;
  updatedByKind: "internal" | "client";
  note: string | null;
  footnote: string | null;
  clarificationNote?: string | null;
}

/** `dataYears`: setin göstergelerine yazılmış değer yılları (yıl penceresi varsayılanı + seçenek aralığı). */
export type KpiSetWithCount = KpiSet & { indicatorCount: number; dataYears?: number[] };

export interface KpiResolvedResponse {
  grid: KpiResolvedGrid;
  cycle: string[] | null;
  fill: SetFillStatus;
}

// ─── Katalog ────────────────────────────────────────────────────────────────
export async function listKpiMetrics(
  opts: { clientId?: string; includeDeprecated?: boolean } = {},
): Promise<{
  metrics: KpiMetric[];
  requirements: (KpiMetricRequirement & { metricId: string })[];
}> {
  const q = new URLSearchParams();
  if (opts.clientId) q.set("clientId", opts.clientId);
  if (opts.includeDeprecated) q.set("includeDeprecated", "true");
  const qs = q.toString();
  return api(`/kpi/metrics${qs ? `?${qs}` : ""}`);
}

export async function createKpiMetric(
  body: KpiMetricUpsert & { clientId?: string | null },
): Promise<KpiMetric> {
  const res = await api<{ metric: KpiMetric }>("/kpi/metrics", { method: "POST", body });
  return res.metric;
}

/** Katalog kardeş sıralaması — tek istek, tek transaction (rev-2 #4b/#12b/#14). */
export async function reorderKpiMetrics(
  parentId: string | null,
  orderedIds: string[],
): Promise<KpiMetricReorderResult> {
  return api<KpiMetricReorderResult>("/kpi/metrics/reorder", {
    method: "POST",
    body: { parentId, orderedIds },
  });
}

export async function updateKpiMetric(
  id: string,
  body: Partial<KpiMetricUpsert>,
): Promise<KpiMetric> {
  const res = await api<{ metric: KpiMetric }>(`/kpi/metrics/${id}`, { method: "PATCH", body });
  return res.metric;
}

export async function renameKpiMetricCode(
  id: string,
  code: string,
  dryRun: boolean,
): Promise<{ metric: KpiMetric; result: KpiMetricRenameCodeResult }> {
  return api(`/kpi/metrics/${id}/rename-code`, { method: "POST", body: { code, dryRun } });
}

// ─── Birim varyantı (DR4/DR10) ──────────────────────────────────────────────
// 409: KPI_UNIT_IN_USE | KPI_UNIT_FORMULA_CONFLICT | KPI_UNIT_DEPENDENTS (ApiError.code, details)

/** Etki önizlemesi — yazmaz; seçici değişince anında çağrılır. */
export async function previewKpiUnitChange(body: KpiUnitChangeBody): Promise<KpiUnitChangePreview> {
  return api("/kpi/unit-change/preview", { method: "POST", body });
}

/** Müşteri × gösterge kalıcı birimi; kullanımda + çevrilebilirde `mode` zorunlu (DR8). */
export async function applyKpiUnitChange(body: KpiUnitChangeBody): Promise<KpiUnitChangeResult> {
  return api("/kpi/unit-change", { method: "POST", body });
}

/** Müşterinin kalıcı birim kayıtları — set kurulumu "Birim tercihleri" adımı (TODO-KPI-97). */
export async function listKpiClientMetricUnits(
  clientId: string,
): Promise<{ units: { metricId: string; unit: string }[] }> {
  return api(`/kpi/clients/${clientId}/metric-units`);
}

/** Verilen göstergelerden müşteride değer/hedef taşıyanlar (tek istek). */
export async function getKpiClientUnitsInUse(
  clientId: string,
  metricIds: readonly string[],
): Promise<{ inUse: string[] }> {
  return api(`/kpi/clients/${clientId}/metric-units/usage`, {
    method: "POST",
    body: { metricIds },
  });
}

/** Katalog varsayılanı değişimi öncesi müşteri bazında kullanım (DR10). */
export async function getKpiMetricUnitUsage(metricId: string): Promise<KpiUnitUsage> {
  return api(`/kpi/metrics/${metricId}/unit-usage`);
}

export async function deprecateKpiMetric(
  id: string,
): Promise<{ outcome: "deprecated" | "deleted"; valueCount: number }> {
  return api(`/kpi/metrics/${id}`, { method: "DELETE" });
}

// ─── Kırılım hiyerarşisi (spec §6.3/§6.4, routes/kpi-breakdown.ts — kpi.manage) ──────────────
// Hata kodları KPI_BREAKDOWN_ERROR_CODES (ApiError.code) → metin lib/kpi-breakdown-copy.ts.

/** "Kırılıma dönüştür" — `dryRun: true` önizler (yazmaz), aynı sayıları döner. */
export async function convertKpiBreakdown(
  metricId: string,
  body: KpiBreakdownConvertBody,
): Promise<KpiBreakdownConvertResult> {
  return api(`/kpi/metrics/${metricId}/breakdown/convert`, { method: "POST", body });
}

/** Grup metaverisi (tamlık / toplama kuralı / boyut) değişiminin etkisi — yazmaz (R10). */
export async function previewKpiBreakdownMetadata(
  groupMetricId: string,
  next: Pick<KpiBreakdown, "complete" | "rollup"> & { dimension?: string },
): Promise<{ clients: number; values: number }> {
  const q = new URLSearchParams({ complete: String(next.complete), rollup: next.rollup });
  if (next.dimension) q.set("dimension", next.dimension);
  return api(`/kpi/metrics/${groupMetricId}/breakdown/preview-metadata?${q.toString()}`);
}

/** BreakdownGroupModal kaydı (tek tx): yeni grup ya da mevcut gruba üye; `dryRun` önizler. */
export async function createKpiBreakdownGroup(
  totalMetricId: string,
  body: KpiBreakdownGroupCreateBody,
): Promise<KpiBreakdownGroupCreateResult> {
  return api(`/kpi/metrics/${totalMetricId}/breakdown/groups`, { method: "POST", body });
}

/** Benzer boyut (sunucu `similarDimension` — hazır, Diğer hariç ∪ özel). */
export interface KpiBreakdownDimensionRef {
  slug: string;
  label: string;
}

/** Kırılım boyut sözlüğü (§19 B2/B3): hazır + özel boyutlar; `similarTo` → benzer ad da. */
export async function listKpiBreakdownDimensions(
  similarTo?: string,
): Promise<KpiBreakdownDimensionsResponse & { similar?: KpiBreakdownDimensionRef | null }> {
  const q = similarTo ? `?${new URLSearchParams({ similarTo }).toString()}` : "";
  return api(`/kpi/breakdown/dimensions${q}`);
}

/** K-B4: özel boyut düzenle (slug değişmez; hazır slug 422 `breakdown_dimension_preset`). */
export async function updateKpiBreakdownDimension(
  slug: string,
  body: KpiBreakdownCustomDimensionPatch,
): Promise<KpiBreakdownCustomDimension> {
  const r = await api<{ dimension: KpiBreakdownCustomDimension }>(
    `/kpi/breakdown/dimensions/${encodeURIComponent(slug)}`,
    { method: "PATCH", body },
  );
  return r.dimension;
}

/** K-B4: özel boyut sil — kullanan grup varsa 409 `breakdown_dimension_in_use` (`details.groups`). */
export async function deleteKpiBreakdownDimension(slug: string): Promise<void> {
  await api(`/kpi/breakdown/dimensions/${encodeURIComponent(slug)}`, { method: "DELETE" });
}

/** Danışman: 0069 sonrası değerli kategori satırları (metrik kimlikleri) — TODO-KPI-116. */
export async function getKpiLegacyCategoryValues(setId: string): Promise<{ metricIds: string[] }> {
  return api(`/kpi/sets/${setId}/legacy-category-values`);
}

/** Set ayarı "Raporda kırılımları göster" (DOCX; false → yalnız toplam satırları). */
export async function setKpiReportBreakdowns(setId: string, enabled: boolean): Promise<KpiSet> {
  return updateKpiSet(setId, { reportBreakdowns: enabled });
}

/** Toplamın set satırında açık hesap grubu (DR3); null = otomatik (ilk tam grup). */
export async function setKpiCalculationGroup(
  setIndicatorId: string,
  groupMetricId: string | null,
): Promise<KpiSetIndicator> {
  return (await updateKpiSetIndicator(setIndicatorId, { calculationGroupMetricId: groupMetricId }))
    .indicator;
}

// ─── Müşteri özeti / setler ─────────────────────────────────────────────────
export interface KpiClientsSummaryResponse {
  clients: KpiClientSummary[];
  total: number;
}

/**
 * ClientPicker işi (2026-10-07): özet artık sayfalı — `search` + `limit` (varsayılan 50) ya da
 * `ids` (seçili/son kullanılan müşterileri tamamlamak için; arama yok sayılır).
 */
export async function listKpiClientsSummary(
  opts: { search?: string; limit?: number; ids?: readonly string[] } = {},
): Promise<KpiClientsSummaryResponse> {
  const params = new URLSearchParams();
  if (opts.ids && opts.ids.length > 0) params.set("ids", opts.ids.join(","));
  else if (opts.search?.trim()) params.set("search", opts.search.trim());
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return api<KpiClientsSummaryResponse>(`/kpi/clients-summary${qs ? `?${qs}` : ""}`);
}

/** Picker satırı: ikincil satır set sayısı ("3 set" / "Henüz gösterge seti yok"). */
export function kpiClientPickerItem(c: KpiClientSummary): ClientPickerItem {
  return {
    id: c.id,
    name: c.name,
    sector: c.sector,
    secondary: c.setCount === 0 ? "Henüz veri seti yok" : `${c.setCount} veri seti`,
  };
}

/** `ClientPicker.fetchOptions` — KPI bağlam çubuğu / fork modalı set bilgisiyle arar. */
export async function searchKpiClients(
  query: string,
  limit = 20,
): Promise<{ items: ClientPickerItem[]; total: number }> {
  const res = await listKpiClientsSummary({ search: query, limit });
  return { items: res.clients.map(kpiClientPickerItem), total: res.total };
}

/** TODO-KPI-29 — set "Çerçeve" seçicisi: yayındaki sürümü olan çerçeveler (kpi.view yeter). */
export async function listKpiFrameworks(): Promise<KpiFrameworkOption[]> {
  const res = await api<{ frameworks: KpiFrameworkOption[] }>("/kpi/frameworks");
  return res.frameworks;
}

export async function listKpiSets(clientId: string): Promise<KpiSetWithCount[]> {
  const res = await api<{ sets: KpiSetWithCount[] }>(`/kpi/clients/${clientId}/sets`);
  return res.sets;
}

export async function forkKpiSet(
  clientId: string,
  body: Omit<KpiSetCreate, "clientId">,
): Promise<{ setId: string; indicatorCount: number }> {
  return api(`/kpi/clients/${clientId}/sets/fork`, { method: "POST", body });
}

export async function getKpiSet(
  setId: string,
): Promise<{ set: KpiSet; indicators: KpiSetIndicator[] }> {
  return api(`/kpi/sets/${setId}`);
}

export async function updateKpiSet(setId: string, body: KpiSetUpdate): Promise<KpiSet> {
  const res = await api<{ set: KpiSet }>(`/kpi/sets/${setId}`, { method: "PATCH", body });
  return res.set;
}

export async function deleteKpiSet(setId: string): Promise<void> {
  await api<void>(`/kpi/sets/${setId}`, { method: "DELETE" });
}

export async function getKpiResolved(
  setId: string,
  opts: { years: number[]; entityIds?: string[] },
): Promise<KpiResolvedResponse> {
  const q = new URLSearchParams();
  if (opts.years.length > 0) q.set("years", opts.years.join(","));
  if (opts.entityIds && opts.entityIds.length > 0) q.set("entityIds", opts.entityIds.join(","));
  return api(`/kpi/sets/${setId}/resolved?${q.toString()}`);
}

// ─── Set göstergeleri ───────────────────────────────────────────────────────
export async function addKpiSetIndicator(
  setId: string,
  body: { metricId: string; parentId?: string | null; orderIndex?: number },
): Promise<KpiSetIndicator> {
  const res = await api<{ indicator: KpiSetIndicator }>(`/kpi/sets/${setId}/indicators`, {
    method: "POST",
    body,
  });
  return res.indicator;
}

/**
 * Set göstergesi PATCH'i. `converted` (TODO-KPI-112): `calculationGroupMetricId` yazımı havuzdaki ham
 * toplam değerleri raporlanan değere çevirdiyse dönüşen değer sayısı (yoksa 0).
 */
export async function updateKpiSetIndicator(
  id: string,
  body: KpiSetIndicatorPatch,
): Promise<{ indicator: KpiSetIndicator; converted: number }> {
  const res = await api<{ indicator: KpiSetIndicator; converted?: number }>(
    `/kpi/set-indicators/${id}`,
    { method: "PATCH", body },
  );
  return { indicator: res.indicator, converted: res.converted ?? 0 };
}

export async function removeKpiSetIndicator(id: string): Promise<{ removed: number }> {
  return api(`/kpi/set-indicators/${id}`, { method: "DELETE" });
}

export async function setKpiIndicatorChecked(
  id: string,
  checked: boolean,
): Promise<KpiSetIndicator> {
  const res = await api<{ indicator: KpiSetIndicator }>(`/kpi/set-indicators/${id}/checked`, {
    method: "POST",
    body: { checked },
  });
  return res.indicator;
}

// ─── Değerler ───────────────────────────────────────────────────────────────
export async function putKpiValue(body: KpiValueUpsert): Promise<KpiValueDto> {
  const res = await api<{ value: KpiValueDto }>("/kpi/values", { method: "PUT", body });
  return res.value;
}

/** M17 — hücrenin sürüm tarihçesi (en yeni önce); yalnız iç aktör (portalda yok). */
export async function listKpiValueVersions(valueId: string): Promise<KpiValueVersion[]> {
  const res = await api<{ versions: KpiValueVersion[] }>(`/kpi/values/${valueId}/versions`);
  return res.versions;
}

/**
 * TODO-KPI-23: `numericValue: null` = raporlanan değer XXX (değer bekleniyor). D20: `unit` zorunlu
 * (yazarın gördüğü birim; uyuşmazsa 409 KPI_UNIT_CHANGED `details.current`).
 */
export async function putKpiReportedValue(body: {
  unit: string | null;
  metricId: string;
  entityId: string;
  year: number;
  numericValue: number | null;
  reportedBasis?: string | null;
  pinned?: boolean;
}): Promise<KpiValueDto> {
  const res = await api<{ value: KpiValueDto }>("/kpi/values/reported", { method: "POST", body });
  return res.value;
}

export async function verifyKpiValue(
  valueId: string,
  expectedVersion: number,
): Promise<KpiValueDto> {
  const res = await api<{ value: KpiValueDto }>(`/kpi/values/${valueId}/verify`, {
    method: "POST",
    body: { expectedVersion },
  });
  return res.value;
}

export async function verifyKpiValues(
  items: { valueId: string; expectedVersion: number }[],
): Promise<{ verified: string[]; conflicts: KpiValueDto[] }> {
  return api("/kpi/values/verify-bulk", { method: "POST", body: { items } });
}

export async function requestKpiClarification(
  valueId: string,
  expectedVersion: number,
  clientNote: string,
): Promise<KpiValueDto> {
  const res = await api<{ value: KpiValueDto }>(`/kpi/values/${valueId}/clarification`, {
    method: "POST",
    body: { expectedVersion, clientNote },
  });
  return res.value;
}

export async function setKpiValuePinned(valueId: string, pinned: boolean): Promise<KpiValueDto> {
  const res = await api<{ value: KpiValueDto }>(`/kpi/values/${valueId}/pinned`, {
    method: "POST",
    body: { pinned },
  });
  return res.value;
}

// ─── Formül varyantı talepleri (T19 — DR7/C10; routes/kpi-variants.ts) ─────────
export interface KpiPendingVariant {
  indicatorId: string;
  setId: string;
  metricId: string;
  code: string;
  label: string;
  activeFormula: { id: string; label: string; expression: string } | null;
  pendingFormula: { id: string; label: string; expression: string } | null;
  requestedBy: { id: string; fullName: string } | null;
  requestedAt: string | null;
  /** TODO-KPI-14 — son 2 yıl: mevcut → talep edilen varyantla (konsolide; hesaplanamıyorsa null). */
  impact: { year: number; current: number | null; proposed: number | null }[];
}

/** Onay bekleyen değerlerin yılları — yıl aralığından bağımsız (Özet kuyruğu "aralık dışı" uyarısı). */
export async function listKpiPendingYears(setId: string): Promise<number[]> {
  const res = await api<{ years: number[] }>(`/kpi/sets/${setId}/pending-years`);
  return res.years;
}

export async function listKpiPendingVariants(setId: string): Promise<KpiPendingVariant[]> {
  const res = await api<{ variants: KpiPendingVariant[] }>(`/kpi/sets/${setId}/pending-variants`);
  return res.variants;
}

export async function verifyKpiVariant(
  indicatorId: string,
  approve: boolean,
): Promise<KpiSetIndicator> {
  const res = await api<{ indicator: KpiSetIndicator }>(
    `/kpi/set-indicators/${indicatorId}/variant-verify`,
    { method: "POST", body: { approve } },
  );
  return res.indicator;
}

// Sektör paketi uçları api/kpi-packages.ts'te (yanıtlar shared Zod şemalarıyla ayrıştırılır);
// mevcut içe aktarmalar bozulmasın diye buradan yeniden dışa açılır.
export * from "./kpi-packages";
